import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FORM_ERROR } from "final-form";

import Logo from "./Logo";
import { actions as emailActions } from "coupon-common/src/modules/email";
import { dispatchAsAsync } from "coupon-common/src/utils";

/*
  "Can't find your address?" support modal for the business signup page.

  Same email action and result handling as the Contact Support modal on the
  blogs page (emailActions.sendContactMessage), but with fields and copy
  specific to address-lookup problems. The address/business the user was
  working on are prefilled from the signup form and folded into the message
  body, so support sees exactly what they were trying to enter.

  Rendered OUTSIDE the signup <Form> (see BusinessDetails.jsx) so its own
  <form> is never nested inside another one.
*/

const RED = "#C0392B";

// Backend validates/records the subject the same way as the blogs-page
// modal, so reuse one of its existing values. The address context is in
// the message body.
const SUBJECT = "technical";

const labelStyle = {
  display: "block", fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6,
};

const inputStyle = {
  width: "100%", boxSizing: "border-box",
  border: "1.5px solid #e5e7eb", borderRadius: 8,
  padding: "10px 12px", fontSize: 13, color: "#111",
  outline: "none", background: "#fff", fontFamily: "inherit",
  transition: "border-color .15s",
};

const focusRed = e => { e.target.style.borderColor = RED; };
const blurGray = e => { e.target.style.borderColor = "#e5e7eb"; };

export default function AddressHelpModal({ initial, dispatch, onClose }) {
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [business, setBusiness] = useState(initial?.business || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    if (!name.trim() || !email.trim() || !address.trim()) return;

    setSubmitError(null);
    setSubmitting(true);

    const message = [
      "Address lookup issue (business signup)",
      "",
      `Business: ${business.trim() || "(not provided)"}`,
      `Address they are trying to enter: ${address.trim()}`,
      "",
      details.trim() || "(no additional details)",
    ].join("\n");

    try {
      const result = await dispatchAsAsync(
        emailActions.sendContactMessage({
          name: name.trim(),
          email: email.trim(),
          subject: SUBJECT,
          message,
        }),
        dispatch,
      );

      // Resolves with nothing on success, with an error payload otherwise.
      if (!result) {
        setSent(true);
      } else if (result[FORM_ERROR]) {
        setSubmitError(result[FORM_ERROR]);
      } else {
        const text = Object.entries(result)
          .map(([field, msg]) => `${field}: ${Array.isArray(msg) ? msg.join(", ") : msg}`)
          .join(" ");
        setSubmitError(text || "Something went wrong. Please try again.");
      }
    } catch (err) {
      console.error("[AddressHelpModal] submit threw:", err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(17,24,39,0.55)",
        backdropFilter: "blur(3px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1100, padding: 20,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Help finding your address"
        style={{
          position: "relative",
          width: "100%", maxWidth: 860, maxHeight: "90vh", overflowY: "auto",
          borderRadius: 24, background: "#fff",
          boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
          display: "flex", flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 20, right: 20, zIndex: 1,
            width: 34, height: 34, borderRadius: "50%",
            border: "1.5px solid #e5e7eb", background: "#fff",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 1l12 12M13 1L1 13" stroke="#555" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>

        {/* Left — pitch + email */}
        <div style={{
          flex: "1 1 320px", padding: "48px 44px",
          display: "flex", flexDirection: "column", justifyContent: "center",
        }}
        >
          <div style={{ alignSelf: "flex-start", marginBottom: 20 }}>
            <Logo />
          </div>
          <div style={{
            fontSize: 12, fontWeight: 700, letterSpacing: "0.08em",
            color: RED, textTransform: "uppercase", marginBottom: 16,
          }}
          >
            Address help
          </div>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: "#1a1a1a", lineHeight: 1.25, margin: "0 0 14px" }}>
            Can&apos;t find your <span style={{ color: RED }}>address</span>?
          </h2>
          <p style={{ fontSize: 14, color: "#777", lineHeight: 1.6, margin: "0 0 28px" }}>
            Tell us the address you&apos;re trying to enter and our team will get your
            business set up. Suites, units, new buildings and rural addresses can
            sometimes be missed by the lookup.
          </p>

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{
              width: 36, height: 36, borderRadius: "50%", background: "#fbe4e1",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <rect x="1.5" y="3" width="13" height="10" rx="1.5" stroke={RED} strokeWidth="1.3" />
                <path d="M1.5 4l6.5 4.5L14.5 4" stroke={RED} strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </span>
            <div>
              <div style={{ fontSize: 11.5, color: "#999" }}>Prefer email?</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#1a1a1a" }}>support@mycouponbook.ca</div>
            </div>
          </div>
        </div>

        {/* Right — form card */}
        <div style={{
          flex: "1 1 360px", background: "#f7f4f4",
          padding: 28, display: "flex", alignItems: "center",
        }}
        >
          <div style={{
            width: "100%", background: "#fff", borderRadius: 20,
            padding: 28, boxShadow: "0 16px 40px rgba(0,0,0,0.08)",
          }}
          >
            {sent ? (
              <div style={{ textAlign: "center", padding: "24px 4px" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1a1a1a", marginBottom: 8 }}>
                  Message sent
                </div>
                <p style={{ fontSize: 13, color: "#777", margin: "0 0 18px" }}>
                  Thanks! We&apos;ll look into your address and reply to the email you provided.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    border: "none", borderRadius: 999, padding: "11px 28px",
                    background: RED, color: "#fff",
                    fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-addr-name" style={labelStyle}>Name</label>
                  <input
                    id="cb-addr-name"
                    type="text"
                    required
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your name"
                    style={inputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-addr-email" style={labelStyle}>Email</label>
                  <input
                    id="cb-addr-email"
                    type="email"
                    required
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@business.com"
                    style={inputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-addr-business" style={labelStyle}>Business name</label>
                  <input
                    id="cb-addr-business"
                    type="text"
                    value={business}
                    onChange={e => setBusiness(e.target.value)}
                    placeholder="Boston Pizza"
                    style={inputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div style={{ marginBottom: 14 }}>
                  <label htmlFor="cb-addr-address" style={labelStyle}>Your business address</label>
                  <input
                    id="cb-addr-address"
                    type="text"
                    required
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Street, suite/unit, city, province, postal code"
                    style={inputStyle}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                <div>
                  <label htmlFor="cb-addr-details" style={labelStyle}>
                    What&apos;s happening? <span style={{ fontWeight: 400, color: "#999" }}>(optional)</span>
                  </label>
                  <textarea
                    id="cb-addr-details"
                    rows={3}
                    value={details}
                    onChange={e => setDetails(e.target.value)}
                    placeholder="e.g. my address doesn't show up, it picks the wrong city, my suite number disappears"
                    style={{ ...inputStyle, resize: "vertical" }}
                    onFocus={focusRed}
                    onBlur={blurGray}
                  />
                </div>

                {submitError && (
                  <div style={{
                    background: "#fff2f2", border: "1px solid #fca5a5",
                    borderRadius: 8, padding: "10px 14px",
                    fontSize: 13, color: RED, marginTop: 14,
                  }}
                  >
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    marginTop: 18, width: "100%",
                    border: "none", borderRadius: 999, padding: "15px 22px",
                    background: "linear-gradient(145deg, #e03030 0%, #b91c1c 100%)",
                    color: "#fff", fontSize: 14, fontWeight: 700,
                    cursor: submitting ? "default" : "pointer",
                    opacity: submitting ? 0.7 : 1,
                    fontFamily: "inherit",
                    boxShadow: "0 8px 20px rgba(192,57,43,0.35)",
                  }}
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

AddressHelpModal.propTypes = {
  initial: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    business: PropTypes.string,
    address: PropTypes.string,
  }),
  dispatch: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};